import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { FlsStore } from "../../stores/fls.store";
import { PreferenceService } from "../../services/preference.service";
import { AlertService } from "../../services/alert.service";
import { s25Resource } from "../../util/s25.resource";
import { S25DropdownPaginatedModule } from "../s25-dropdown/s25.dropdown.paginated.module";
import { S25TriggerRerenderModule } from "../s25-trigger-rerender/s25.trigger.rerender.module";
import { DropDownItem } from "../../pojo/DropDownItem";
import {
    emptySsoMappingRuleForm,
    mergeTypedAndOtherSsoMappingRules,
    parseSsoMappingsPref,
    partitionSsoMappingRulesByType,
    serializeSsoMappingsPref,
    SsoMappingItemForm,
    SsoMappingRule,
    SsoMappingRuleForm,
    ssoMappingRuleLabel,
    ssoMappingRulesToForm,
    ssoMappingUiCopyForType,
    validateSsoMappingForms,
} from "../../pojo/SsoMappingsI";

@TypeManagerDecorator("s25-ng-sso-mappings-admin")
@Component({
    selector: "s25-ng-sso-mappings-admin",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, FormsModule, S25DropdownPaginatedModule, S25TriggerRerenderModule],
    template: `
        <section class="sso-mappings-admin">
            <h2>{{ uiCopy().sectionTitle }}</h2>
            <p class="ngFinePrint sso-mappings-admin__intro">{{ uiCopy().intro }}</p>

            @if (prefResource.isLoading() && prefResource.value() === undefined) {
                <p class="ngFinePrint">Loading SSO mappings…</p>
            } @else if (prefResource.error()) {
                <div class="ngRed ngBold cn-alert cn-alert--danger c-margin-bottom--single" role="alert">
                    <div class="cn-alert__label">
                        <span>Could not load SSO mappings.</span>
                    </div>
                </div>
            } @else {
                @if (!canEdit()) {
                    <p class="ngFinePrint c-margin-bottom--single" role="status">
                        View only. Full Security functional security is required to edit SSO mappings.
                    </p>
                }

                @if (validationError()) {
                    <div class="ngRed ngBold cn-alert cn-alert--danger c-margin-bottom--single" role="alert">
                        <div class="cn-alert__label">
                            <span>{{ validationError() }}</span>
                        </div>
                    </div>
                }

                @if (message()) {
                    <div class="ngGreen ngBold cn-alert cn-alert--success c-margin-bottom--single" role="status">
                        <div class="cn-alert__label">
                            <span>{{ message() }}</span>
                        </div>
                    </div>
                }

                @for (rule of mappingForms(); track trackRule(ruleIndex, rule); let ruleIndex = $index) {
                    <article class="sso-mappings-admin__rule card c-margin-bottom--single">
                        <header class="sso-mappings-admin__rule-header">
                            <label class="sso-mappings-admin__name-field">
                                <span class="ngFinePrint">Name</span>
                                @if (canEdit()) {
                                    <input
                                        type="text"
                                        class="c-input sso-mappings-admin__name-input"
                                        [ngModel]="rule.sso_mapping_name"
                                        [attr.aria-label]="'Name for ' + ruleLabel(rule, ruleIndex)"
                                        [placeholder]="'Rule ' + (ruleIndex + 1)"
                                        (ngModelChange)="patchMappingName(ruleIndex, $event)"
                                    />
                                } @else {
                                    <span class="sso-mappings-admin__name-readonly">{{
                                        ruleLabel(rule, ruleIndex)
                                    }}</span>
                                }
                            </label>
                            @if (canEdit()) {
                                <button
                                    type="button"
                                    class="aw-button aw-button--danger--outline"
                                    (click)="removeRule(ruleIndex)"
                                >
                                    Remove rule
                                </button>
                            }
                        </header>

                        <div class="sso-mappings-admin__section">
                            <h4 class="ngBold">Security groups</h4>
                            <p class="ngFinePrint">
                                SSO group attribute value sent by the identity provider, mapped to a security group.
                            </p>
                            @if (canEdit()) {
                                <ng-container *s25-ng-trigger-rerender="groupsPickerRemountToken(ruleIndex)">
                                    <s25-ng-dropdown-multi-search-criteria
                                        class="sso-mappings-admin__picker"
                                        [type]="'securityGroups'"
                                        [placeholder]="'Add security groups'"
                                        [hasDone]="false"
                                        [value]="groupsPickerSeedByRuleIndex()[ruleIndex]"
                                        [ariaLabel]="'Add security groups to rule ' + (ruleIndex + 1)"
                                        (done)="addGroupsFromPicker(ruleIndex, $event)"
                                    />
                                </ng-container>
                            }
                            @if (rule.sso_groups.length) {
                                <ul class="sso-mappings-admin__rows">
                                    @for (groupRow of rule.sso_groups; track trackItem(groupRow)) {
                                        <li class="sso-mappings-admin__row">
                                            <label class="sso-mappings-admin__field">
                                                <span class="ngFinePrint">SSO attribute value</span>
                                                <input
                                                    type="text"
                                                    class="c-input"
                                                    [ngModel]="groupRow.itemName"
                                                    [disabled]="!canEdit()"
                                                    (ngModelChange)="
                                                        patchGroupItemName(ruleIndex, groupRow.itemId, $event)
                                                    "
                                                />
                                            </label>
                                            <span class="sso-mappings-admin__target-label">
                                                System Label: {{ groupRow.displayName || groupRow.itemId }}
                                            </span>
                                            @if (canEdit()) {
                                                <button
                                                    type="button"
                                                    class="aw-button aw-button--danger--outline"
                                                    (click)="removeGroup(ruleIndex, groupRow.itemId)"
                                                >
                                                    Remove
                                                </button>
                                            }
                                        </li>
                                    }
                                </ul>
                            } @else {
                                <p class="ngFinePrint">No groups in this rule.</p>
                            }
                        </div>

                        <div class="sso-mappings-admin__section">
                            <h4 class="ngBold">Organizations</h4>
                            @if (canEdit()) {
                                <ng-container *s25-ng-trigger-rerender="organizationsPickerRemountToken(ruleIndex)">
                                    <s25-ng-dropdown-multi-search-criteria
                                        class="sso-mappings-admin__picker"
                                        [type]="uiCopy().organizationCriteriaType"
                                        [placeholder]="'Add organizations'"
                                        [hasDone]="false"
                                        [value]="organizationsPickerSeedByRuleIndex()[ruleIndex]"
                                        [ariaLabel]="'Add organizations to rule ' + (ruleIndex + 1)"
                                        (done)="addOrganizationsFromPicker(ruleIndex, $event)"
                                    />
                                </ng-container>
                            }
                            @if (rule.sso_organizations.length) {
                                <ul class="sso-mappings-admin__rows">
                                    @for (organizationRow of rule.sso_organizations; track trackItem(organizationRow)) {
                                        <li class="sso-mappings-admin__row">
                                            <label class="sso-mappings-admin__field">
                                                <span class="ngFinePrint">SSO attribute value</span>
                                                <input
                                                    type="text"
                                                    class="c-input"
                                                    [ngModel]="organizationRow.itemName"
                                                    [disabled]="!canEdit()"
                                                    (ngModelChange)="
                                                        patchOrganizationItemName(
                                                            ruleIndex,
                                                            organizationRow.itemId,
                                                            $event
                                                        )
                                                    "
                                                />
                                            </label>
                                            <span class="sso-mappings-admin__target-label">
                                                System Label:
                                                {{ organizationRow.displayName || organizationRow.itemId }}
                                            </span>
                                            @if (canEdit()) {
                                                <button
                                                    type="button"
                                                    class="aw-button aw-button--danger--outline"
                                                    (click)="removeOrganization(ruleIndex, organizationRow.itemId)"
                                                >
                                                    Remove
                                                </button>
                                            }
                                        </li>
                                    }
                                </ul>
                            } @else {
                                <p class="ngFinePrint">No organizations in this rule.</p>
                            }
                        </div>

                        <div class="sso-mappings-admin__section">
                            <h4 class="ngBold">Contact role</h4>
                            <p class="ngFinePrint">Optional single role criterion for this rule.</p>
                            <div class="sso-mappings-admin__role-stack">
                                @if (canEdit()) {
                                    <div class="sso-mappings-admin__role-picker-row">
                                        <s25-ng-dropdown-search-criteria
                                            class="sso-mappings-admin__picker sso-mappings-admin__role-picker"
                                            type="organizationRoles"
                                            placeholder="Role"
                                            [value]="rolePickerSeedByRuleIndex()[ruleIndex] ?? null"
                                            (valueChange)="onRoleSelected(ruleIndex, $event)"
                                        />
                                        @if (rule.sso_role) {
                                            <button
                                                type="button"
                                                class="aw-button aw-button--outline"
                                                (click)="clearRole(ruleIndex)"
                                            >
                                                Clear role
                                            </button>
                                        }
                                    </div>
                                } @else if (rule.sso_role?.displayName || rule.sso_role?.itemId) {
                                    <span class="sso-mappings-admin__target-label">
                                        System Label: {{ rule.sso_role?.displayName || rule.sso_role?.itemId }}
                                    </span>
                                }
                                <label class="sso-mappings-admin__field sso-mappings-admin__role-field">
                                    <span class="ngFinePrint">SSO attribute value</span>
                                    <input
                                        type="text"
                                        class="c-input"
                                        [ngModel]="rule.sso_role?.itemName ?? ''"
                                        [disabled]="!canEdit()"
                                        (ngModelChange)="patchRoleItemName(ruleIndex, $event)"
                                    />
                                </label>
                            </div>
                        </div>
                    </article>
                } @empty {
                    <p class="ngFinePrint c-margin-bottom--single">{{ uiCopy().emptyRulesMessage }}</p>
                }

                @if (canEdit()) {
                    <div class="sso-mappings-admin__actions">
                        <button type="button" class="aw-button aw-button--outline" (click)="addRule()">Add rule</button>
                        <button
                            type="button"
                            class="aw-button aw-button--primary"
                            [disabled]="saving() || !isDirty()"
                            (click)="save()"
                        >
                            {{ saving() ? "Saving…" : "Save" }}
                        </button>
                        <button
                            type="button"
                            class="aw-button aw-button--outline"
                            [disabled]="saving() || !isDirty()"
                            (click)="cancel()"
                        >
                            Cancel
                        </button>
                    </div>
                }
            }
        </section>
    `,
    styles: `
        :host {
            display: block;
        }

        .sso-mappings-admin__intro {
            max-width: 52rem;
        }

        .sso-mappings-admin__rule {
            padding: 1rem 1.25rem;
        }

        .sso-mappings-admin__rule-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 1rem;
            margin-bottom: 1rem;
        }

        .sso-mappings-admin__name-field {
            display: flex;
            flex-direction: column;
            gap: 0.25rem;
            flex: 1 1 auto;
            min-width: 12rem;
            max-width: 28rem;
            margin: 0;
        }

        .sso-mappings-admin__name-input {
            font-size: 1.1rem;
            font-weight: 600;
        }

        .sso-mappings-admin__name-readonly {
            font-size: 1.1rem;
            font-weight: 600;
        }

        .sso-mappings-admin__section {
            margin-bottom: 1.25rem;
        }

        .sso-mappings-admin__section:last-child {
            margin-bottom: 0;
        }

        .sso-mappings-admin__picker {
            display: block;
            max-width: 28rem;
            margin-bottom: 0.75rem;
        }

        .sso-mappings-admin__rows {
            list-style: none;
            margin: 0;
            padding: 0;
        }

        .sso-mappings-admin__row {
            display: flex;
            flex-wrap: wrap;
            align-items: flex-end;
            gap: 0.75rem 1rem;
            padding: 0.75rem 0;
            border-top: 1px solid var(--color--border-subtle, #e5e5e5);
        }

        .sso-mappings-admin__field {
            display: flex;
            flex-direction: column;
            gap: 0.25rem;
            min-width: 14rem;
            flex: 1 1 14rem;
        }

        .sso-mappings-admin__target-label {
            flex: 1 1 auto;
            padding-bottom: 0.35rem;
        }

        .sso-mappings-admin__role-stack {
            display: flex;
            flex-direction: column;
            gap: 0.75rem;
            max-width: 28rem;
        }

        .sso-mappings-admin__role-picker-row {
            display: flex;
            flex-wrap: wrap;
            align-items: flex-end;
            gap: 0.5rem;
        }

        .sso-mappings-admin__role-stack .sso-mappings-admin__picker {
            margin-bottom: 0;
        }

        .sso-mappings-admin__role-field {
            flex: none;
            min-width: 0;
            width: 100%;
        }

        .sso-mappings-admin__role-picker {
            flex: 1 1 16rem;
            min-width: 16rem;
        }

        .sso-mappings-admin__actions {
            display: flex;
            flex-wrap: wrap;
            gap: 0.5rem;
            margin-top: 1rem;
        }
    `,
})
export class SsoMappingsAdminComponent {
    readonly mappingType = input.required<string>();

    protected readonly flsStore = inject(FlsStore);

    readonly uiCopy = computed(() => ssoMappingUiCopyForType(this.mappingType()));

    readonly prefResource = s25Resource({
        loader: () =>
            PreferenceService.getPreferences(["sso_mappings"], "S").then((preferences) =>
                parseSsoMappingsPref(preferences?.sso_mappings?.value),
            ),
    });

    readonly mappingForms = signal<SsoMappingRuleForm[]>([]);
    readonly otherMappingRules = signal<SsoMappingRule[]>([]);
    readonly savedSnapshot = signal("");
    readonly saving = signal(false);
    readonly message = signal<string | null>(null);
    readonly validationError = signal<string | null>(null);

    /**
     * Multi-select pickers keep their own checked-item list (`value`) separate from the rule rows
     * rendered below (sso_groups / sso_organizations). We must keep them aligned when either side changes.
     *
     * - **Seed** (`*PickerSeedByRuleIndex`): a stable DropDownItem[] snapshot of what should be checked,
     *   one array per rule index. Updated only when that rule's mapping rows change (picker close, Remove row,
     *   load, cancel). Do not bind `mappingItemsToDropDown(rule…)` inline in the template — a new `[]` every
     *   change-detection cycle resets the picker while it is open.
     * - **Epoch** (`*PickerEpochByRuleIndex`): a monotonic counter per rule, bumped together with the seed.
     *   Fed into `*s25-ng-trigger-rerender` so the picker component is destroyed and recreated with the new seed
     *   (e.g. after Remove, so stale checkboxes are not left in the dropdown's internal state).
     * - **Role seed** (`rolePickerSeedByRuleIndex`): stable snapshot for the role picker `value` binding — do not
     *   call `roleToDropDownItem` inline in the template (new object every CD resets the dropdown).
     * - **Rule rows** remain the source of truth for save; `(done)` calls `syncPickerItems` to merge the
     *   picker's emission into the rule (add new ids, drop unchecked ids, preserve SSO attribute text on rows
     *   that stay selected).
     */
    readonly groupsPickerSeedByRuleIndex = signal<Readonly<Record<number, DropDownItem[]>>>({});
    readonly organizationsPickerSeedByRuleIndex = signal<Readonly<Record<number, DropDownItem[]>>>({});
    readonly rolePickerSeedByRuleIndex = signal<Readonly<Record<number, DropDownItem<number> | null>>>({});
    private readonly groupsPickerEpochByRuleIndex = signal<Readonly<Record<number, number>>>({});
    private readonly organizationsPickerEpochByRuleIndex = signal<Readonly<Record<number, number>>>({});

    readonly canEdit = computed(() => this.flsStore.hasFull("SECURITY"));

    readonly isDirty = computed(
        () => this.serializeSavedState(this.mappingForms(), this.otherMappingRules()) !== this.savedSnapshot(),
    );

    constructor() {
        effect(() => {
            void this.flsStore.ensureLoaded();
        });

        effect(() => {
            const parsedRules = this.prefResource.value();
            const mappingType = this.mappingType();
            if (parsedRules === undefined) {
                return;
            }
            const { otherRules } = partitionSsoMappingRulesByType(parsedRules, mappingType);
            const forms = ssoMappingRulesToForm(parsedRules, mappingType);
            const nextSnapshot = this.serializeSavedState(forms, otherRules);
            if (nextSnapshot === this.savedSnapshot()) {
                return;
            }
            this.otherMappingRules.set(otherRules);
            this.mappingForms.set(forms);
            this.savedSnapshot.set(nextSnapshot);
            this.reseedAllPickerState(forms);
        });
    }

    /** Changes when `refreshGroupsPickerSeed` runs so `*s25-ng-trigger-rerender` remounts that rule's picker. */
    groupsPickerRemountToken(ruleIndex: number): string {
        const epoch = this.groupsPickerEpochByRuleIndex()[ruleIndex] ?? 0;
        return `groups-${ruleIndex}-${epoch}`;
    }

    organizationsPickerRemountToken(ruleIndex: number): string {
        const epoch = this.organizationsPickerEpochByRuleIndex()[ruleIndex] ?? 0;
        return `organizations-${ruleIndex}-${epoch}`;
    }

    trackRule(index: number, _rule: SsoMappingRuleForm): number {
        return index;
    }

    ruleLabel(rule: SsoMappingRuleForm, ruleIndex: number): string {
        return ssoMappingRuleLabel(rule, ruleIndex);
    }

    patchMappingName(ruleIndex: number, mappingName: string): void {
        this.updateForms((forms) => {
            const targetRule = forms[ruleIndex];
            if (targetRule) {
                targetRule.sso_mapping_name = mappingName;
            }
        });
    }

    trackItem(item: SsoMappingItemForm): string {
        return `${item.itemId ?? "new"}-${item.itemName}`;
    }

    addRule(): void {
        this.mappingForms.update((forms) => [...forms, emptySsoMappingRuleForm(this.mappingType())]);
        const newRuleIndex = this.mappingForms().length - 1;
        this.refreshGroupsPickerSeed(newRuleIndex, []);
        this.refreshOrganizationsPickerSeed(newRuleIndex, []);
        this.setRolePickerSeed(newRuleIndex, null);
        this.clearFeedback();
    }

    async removeRule(ruleIndex: number): Promise<void> {
        if (!(await AlertService.confirm("Remove this SSO mapping rule?"))) {
            return;
        }
        this.mappingForms.update((forms) => forms.filter((_, index) => index !== ruleIndex));
        this.reseedAllPickerState(this.mappingForms());
        this.clearFeedback();
    }

    addGroupsFromPicker(ruleIndex: number, selectedItems: DropDownItem[] | null): void {
        if (!this.canEdit()) {
            return;
        }
        this.syncPickerItems(ruleIndex, "sso_groups", selectedItems ?? []);
    }

    addOrganizationsFromPicker(ruleIndex: number, selectedItems: DropDownItem[] | null): void {
        if (!this.canEdit()) {
            return;
        }
        this.syncPickerItems(ruleIndex, "sso_organizations", selectedItems ?? []);
    }

    patchGroupItemName(ruleIndex: number, rowItemId: number, itemName: string): void {
        this.updateForms((forms) => {
            const targetRule = forms[ruleIndex];
            const targetRow = targetRule?.sso_groups.find((group) => group.itemId === rowItemId);
            if (targetRow) {
                targetRow.itemName = itemName;
            }
        });
    }

    patchOrganizationItemName(ruleIndex: number, rowItemId: number, itemName: string): void {
        this.updateForms((forms) => {
            const targetRule = forms[ruleIndex];
            const targetRow = targetRule?.sso_organizations.find((organization) => organization.itemId === rowItemId);
            if (targetRow) {
                targetRow.itemName = itemName;
            }
        });
    }

    patchRoleItemName(ruleIndex: number, itemName: string): void {
        this.updateForms((forms) => {
            const targetRule = forms[ruleIndex];
            if (!targetRule) {
                return;
            }
            if (!itemName.trim() && !targetRule.sso_role?.itemId) {
                targetRule.sso_role = null;
            } else {
                targetRule.sso_role = {
                    itemId: targetRule.sso_role?.itemId,
                    itemName,
                    displayName: targetRule.sso_role?.displayName,
                };
            }
        });
    }

    onRoleSelected(ruleIndex: number, selected: DropDownItem | null): void {
        this.updateForms((forms) => {
            const targetRule = forms[ruleIndex];
            if (!targetRule) {
                return;
            }
            if (!selected?.itemId) {
                targetRule.sso_role = null;
                return;
            }
            const organizationRoleId = Number(selected.itemId);
            const displayName = selected.itemName ?? selected.txt ?? "";
            targetRule.sso_role = {
                itemId: organizationRoleId,
                itemName: targetRule.sso_role?.itemName?.trim() ? targetRule.sso_role.itemName : displayName,
                displayName,
            };
        });
        this.setRolePickerSeed(ruleIndex, this.mappingForms()[ruleIndex]?.sso_role ?? null);
    }

    clearRole(ruleIndex: number): void {
        this.updateForms((forms) => {
            const targetRule = forms[ruleIndex];
            if (targetRule) {
                targetRule.sso_role = null;
            }
        });
        this.setRolePickerSeed(ruleIndex, null);
    }

    removeGroup(ruleIndex: number, rowItemId: number): void {
        this.updateForms((forms) => {
            const targetRule = forms[ruleIndex];
            if (targetRule) {
                targetRule.sso_groups = targetRule.sso_groups.filter((group) => group.itemId !== rowItemId);
            }
        });
        this.refreshGroupsPickerSeed(ruleIndex, this.mappingForms()[ruleIndex]?.sso_groups ?? []);
    }

    removeOrganization(ruleIndex: number, rowItemId: number): void {
        this.updateForms((forms) => {
            const targetRule = forms[ruleIndex];
            if (targetRule) {
                targetRule.sso_organizations = targetRule.sso_organizations.filter(
                    (organization) => organization.itemId !== rowItemId,
                );
            }
        });
        this.refreshOrganizationsPickerSeed(ruleIndex, this.mappingForms()[ruleIndex]?.sso_organizations ?? []);
    }

    cancel(): void {
        const snapshot = this.savedSnapshot();
        if (!snapshot) {
            this.mappingForms.set([]);
            this.reseedAllPickerState([]);
            this.clearFeedback();
            return;
        }
        const parsed = JSON.parse(snapshot) as { forms: SsoMappingRuleForm[]; otherRules: SsoMappingRule[] };
        const forms = parsed.forms ?? [];
        this.mappingForms.set(forms);
        this.otherMappingRules.set(parsed.otherRules ?? []);
        this.reseedAllPickerState(forms);
        this.clearFeedback();
    }

    async save(): Promise<void> {
        if (!this.canEdit() || this.saving()) {
            return;
        }

        const forms = this.mappingForms();
        const validationMessage = validateSsoMappingForms(forms);
        if (validationMessage) {
            this.validationError.set(validationMessage);
            this.message.set(null);
            return;
        }

        this.saving.set(true);
        this.validationError.set(null);
        this.message.set(null);

        try {
            const rules = mergeTypedAndOtherSsoMappingRules(forms, this.otherMappingRules());
            const preferenceValue = serializeSsoMappingsPref(rules);
            await PreferenceService.setPreference("sso_mappings", preferenceValue, "S");
            this.savedSnapshot.set(this.serializeSavedState(forms, this.otherMappingRules()));
            this.message.set("SSO mappings saved.");
            await this.prefResource.reloadAndAwait();
        } catch {
            this.validationError.set("Could not save SSO mappings. Check your permissions and try again.");
        } finally {
            this.saving.set(false);
        }
    }

    /**
     * Applies the picker's checked list to the rule (hasDone=false → runs on dropdown close).
     * Rebuilds the rule list from `selectedItems`: keep existing rows for ids still selected (preserves
     * edited SSO attribute values), add rows for newly selected ids, omit ids no longer selected.
     */
    private syncPickerItems(
        ruleIndex: number,
        listKey: "sso_groups" | "sso_organizations",
        selectedItems: DropDownItem[],
    ): void {
        const selectedIds = new Set<number>();
        for (const selected of selectedItems) {
            const itemId = selected.itemId != null ? Number(selected.itemId) : NaN;
            if (!Number.isNaN(itemId)) {
                selectedIds.add(itemId);
            }
        }

        this.updateForms((forms) => {
            const rule = forms[ruleIndex];
            if (!rule) {
                return;
            }
            const existingById = new Map(
                rule[listKey]
                    .filter((row) => row.itemId != null && selectedIds.has(row.itemId))
                    .map((row) => [row.itemId as number, row]),
            );
            const syncedList: SsoMappingItemForm[] = [];
            for (const selected of selectedItems) {
                const itemId = selected.itemId != null ? Number(selected.itemId) : NaN;
                if (Number.isNaN(itemId)) {
                    continue;
                }
                const existingRow = existingById.get(itemId);
                if (existingRow) {
                    syncedList.push(existingRow);
                    continue;
                }
                const displayName = selected.itemName ?? selected.txt ?? "";
                syncedList.push({
                    itemId,
                    itemName: displayName,
                    displayName,
                });
            }
            rule[listKey] = syncedList;
        });
        // Picker internal state can still list removed ids; re-seed + remount from the rule we just wrote.
        const rule = this.mappingForms()[ruleIndex];
        if (listKey === "sso_groups") {
            this.refreshGroupsPickerSeed(ruleIndex, rule?.sso_groups ?? []);
        } else {
            this.refreshOrganizationsPickerSeed(ruleIndex, rule?.sso_organizations ?? []);
        }
    }

    /** Rule mapping rows → dropdown checkboxes (system label only; not the per-row SSO attribute field). */
    private mappingItemsToDropDown(items: SsoMappingItemForm[]): DropDownItem[] {
        return items
            .filter((row) => row.itemId != null)
            .map((row) => ({
                itemId: row.itemId,
                itemName: row.displayName ?? row.itemName,
            }));
    }

    /** Rebuild seeds and epochs for every rule (load, cancel, delete rule — indices may have shifted). */
    private reseedAllPickerState(forms: SsoMappingRuleForm[]): void {
        const groupsSeed: Record<number, DropDownItem[]> = {};
        const organizationsSeed: Record<number, DropDownItem[]> = {};
        const roleSeed: Record<number, DropDownItem<number> | null> = {};
        const groupsEpoch: Record<number, number> = {};
        const organizationsEpoch: Record<number, number> = {};
        for (let ruleIndex = 0; ruleIndex < forms.length; ruleIndex++) {
            const form = forms[ruleIndex];
            groupsSeed[ruleIndex] = this.mappingItemsToDropDown(form.sso_groups);
            organizationsSeed[ruleIndex] = this.mappingItemsToDropDown(form.sso_organizations);
            roleSeed[ruleIndex] = this.roleToDropDownItem(form.sso_role);
            groupsEpoch[ruleIndex] = (this.groupsPickerEpochByRuleIndex()[ruleIndex] ?? 0) + 1;
            organizationsEpoch[ruleIndex] = (this.organizationsPickerEpochByRuleIndex()[ruleIndex] ?? 0) + 1;
        }
        this.groupsPickerSeedByRuleIndex.set(groupsSeed);
        this.organizationsPickerSeedByRuleIndex.set(organizationsSeed);
        this.rolePickerSeedByRuleIndex.set(roleSeed);
        this.groupsPickerEpochByRuleIndex.set(groupsEpoch);
        this.organizationsPickerEpochByRuleIndex.set(organizationsEpoch);
    }

    /** Update one rule's group picker seed and bump epoch so the template remounts that picker instance. */
    private refreshGroupsPickerSeed(ruleIndex: number, groups: SsoMappingItemForm[]): void {
        this.groupsPickerSeedByRuleIndex.update((seeds) => ({
            ...seeds,
            [ruleIndex]: this.mappingItemsToDropDown(groups),
        }));
        this.groupsPickerEpochByRuleIndex.update((epochs) => ({
            ...epochs,
            [ruleIndex]: (epochs[ruleIndex] ?? 0) + 1,
        }));
    }

    /** Same as {@link refreshGroupsPickerSeed} for the organizations picker. */
    private refreshOrganizationsPickerSeed(ruleIndex: number, organizations: SsoMappingItemForm[]): void {
        this.organizationsPickerSeedByRuleIndex.update((seeds) => ({
            ...seeds,
            [ruleIndex]: this.mappingItemsToDropDown(organizations),
        }));
        this.organizationsPickerEpochByRuleIndex.update((epochs) => ({
            ...epochs,
            [ruleIndex]: (epochs[ruleIndex] ?? 0) + 1,
        }));
    }

    private setRolePickerSeed(ruleIndex: number, role: SsoMappingItemForm | null): void {
        this.rolePickerSeedByRuleIndex.update((seeds) => ({
            ...seeds,
            [ruleIndex]: this.roleToDropDownItem(role),
        }));
    }

    /** Rule role row → dropdown value (system label only; not the SSO attribute field). */
    private roleToDropDownItem(role: SsoMappingItemForm | null): DropDownItem<number> | null {
        if (role?.itemId == null) {
            return null;
        }
        return {
            itemId: role.itemId,
            itemName: role.displayName ?? role.itemName,
        };
    }

    /**
     * Immutably updates mapping forms. Handlers use `ruleIndex` (not `rule` / row object references) because
     * this copy step replaces rule and row instances on every update.
     */
    private updateForms(mutator: (forms: SsoMappingRuleForm[]) => void): void {
        this.mappingForms.update((forms) => {
            const copy = forms.map((form) => ({
                ...form,
                sso_groups: form.sso_groups.map((group) => ({ ...group })),
                sso_organizations: form.sso_organizations.map((organization) => ({ ...organization })),
                sso_role: form.sso_role ? { ...form.sso_role } : null,
            }));
            mutator(copy);
            return copy;
        });
        this.clearFeedback();
    }

    private serializeSavedState(forms: SsoMappingRuleForm[], otherRules: SsoMappingRule[]): string {
        return JSON.stringify({ forms, otherRules });
    }

    private clearFeedback(): void {
        this.validationError.set(null);
        this.message.set(null);
    }
}
